import { useSelector } from 'react-redux';
import type { Parameters, Response } from '../../../../../types/typeHelper';
import { getUniqueProcessNumber, getUniqueProductNumber } from '../../../../helper/processTextHelper';
import { getChannelOPUserId, getPrimaryHouseForChannel } from '../../../../slices/channel/channelSelectors';
import { getSelectedChannelId } from '../../../../slices/channels/channelsSelectors';
import { getIsAgent } from '../../../../slices/currentUser/currentUserSelectors';
import {
  getFilter,
  getSelectedProcessNumber,
  getSelectedProductNumber,
} from '../../../../slices/processes/processesSelectors';
import { useGetProcessDetails, useGetProcesses } from './processes-api';

export const useGetProcessProductsList = (query: Parameters<'/chat/process-details', 'get'>['query']) => {
  return useGetProcessDetails<Response<'/chat/process-details', 'get'>['items'] | undefined>({
    query,
    select: (data) => (data ? data.items : undefined),
  });
};

export const getSelectedProcess = () => {
  const isAgent = useSelector(getIsAgent);
  const selectedChannelId: string = useSelector(getSelectedChannelId);
  const channelOpUserId: string = useSelector(getChannelOPUserId(selectedChannelId));
  const filter = useSelector(getFilter);
  const { name: queryString, type: queryType } = filter || {};
  const houseId: string = useSelector(getPrimaryHouseForChannel(selectedChannelId));

  const processNumber = useSelector(getSelectedProcessNumber);

  const { data } = useGetProcesses({
    query: {
      query_type: queryType,
      query_string: queryString,
      user_id: isAgent ? channelOpUserId : undefined,
      house_id: houseId,
    },
  });
  // This checks are added after the hook as it will break React when it detects
  // different number of hooks
  if (!processNumber) return undefined;
  if (!data) return undefined;

  return data.find(
    ({ ProcessNumber, ProcessSuffix }) => processNumber === getUniqueProcessNumber(ProcessNumber, ProcessSuffix),
  );
};

export const getSelectedProduct = () => {
  const selectedChannelId: string = useSelector(getSelectedChannelId);
  const houseId: string = useSelector(getPrimaryHouseForChannel(selectedChannelId));
  const isAgent: boolean = useSelector(getIsAgent);
  const channelOpUserId: string = useSelector(getChannelOPUserId(selectedChannelId));
  const selectedProcess = getSelectedProcess();
  const selectedProductNumber = useSelector(getSelectedProductNumber);

  const { data: products } = useGetProcessProductsList({
    house_id: houseId,
    process_number: selectedProcess ? selectedProcess.ProcessNumber : '',
    suffix: selectedProcess ? selectedProcess.ProcessSuffix : '',
    user_id: isAgent ? channelOpUserId : undefined,
  });

  if (!selectedProcess) return undefined;
  if (!products) return undefined;

  return products.find(({ ArticleNumber, Id }) => getUniqueProductNumber(ArticleNumber, Id) === selectedProductNumber);
};
